import { getServicesData } from "../components/pages/services/servicesData";

type SiteSearchHit = {
  id: string;
  path: string;
  title: string;
  description: string;
  category: string;
  keywords: string[];
};

/** Fallback if translation key returns the key itself */
function tf(t: (key: string) => string, key: string, fallback: string): string {
  const v = t(key);
  return !v || v === key ? fallback : v;
}

/**
 * Full client-side search index (no API). Extend `extra` when new public pages are added.
 */
export function getSiteSearchHits(t: (key: string) => string): SiteSearchHit[] {
  const fromServices: SiteSearchHit[] = getServicesData(t).map((s) => ({
    id: `svc-${s.id}`,
    path: s.link,
    title: s.title,
    description: s.description,
    category: s.category,
    keywords: [
      ...(s.searchKeywords || []),
      s.title,
      s.description,
      s.category,
    ],
  }));

  const extra: SiteSearchHit[] = [
    {
      id: "page-home",
      path: "/",
      title: tf(t, "home", "الرئيسية"),
      description: tf(t, "servicesPageDesc", ""),
      category: tf(t, "categoryElectronicServices", "خدمات"),
      keywords: ["الرئيسية", "رئيسية", "home", "main", "البدء"],
    },
    {
      id: "page-services",
      path: "/services",
      title: tf(t, "servicesPageTitle", "الخدمات الإلكترونية"),
      description: tf(t, "servicesPageDesc", ""),
      category: tf(t, "categoryElectronicServices", "خدمات"),
      keywords: ["خدمات", "الكترونية", "services", "electronic", "منصة"],
    },
    {
      id: "page-mosque-map",
      path: "/mosques/map",
      title: tf(t, "mosqueMap", "خريطة المساجد"),
      description: tf(t, "mosqueSearchServiceDesc", ""),
      category: tf(t, "categoryElectronicServices", "خدمات"),
      keywords: ["خريطة", "مساجد", "map", "موقع", "google"],
    },
    {
      id: "page-mosque-search-legacy",
      path: "/mosquesearch",
      title: tf(t, "mosqueSearch", "البحث عن المساجد"),
      description: tf(t, "serviceMosqueSearchDesc", ""),
      category: tf(t, "categoryElectronicServices", "خدمات"),
      keywords: ["بحث", "مسجد", "مساجد", "search"],
    },
    {
      id: "page-donation-opp-map",
      path: "/mosque/donation-opportunities-map",
      title: tf(t, "donationOpportunitiesMap", "خريطة فرص المساهمة في النظافة والصيانة"),
      description: tf(t, "serviceMosqueContributionDesc", ""),
      category: tf(t, "categoryElectronicServices", "خدمات"),
      keywords: ["خريطة", "صيانة", "فرص", "مساجد", "map", "opportunities"],
    },
    {
      id: "page-lands-list",
      path: "/benefaction/lands",
      title: tf(t, "availableLandsForMosque", "الأراضي المتاحة لبناء مسجد"),
      description: tf(t, "serviceAvailableLandsDesc", ""),
      category: tf(t, "categoryElectronicServices", "خدمات"),
      keywords: ["أراضي", "أرض", "تبرع", "lands", "list"],
    },
    {
      id: "page-lands-map",
      path: "/benefaction/lands/map",
      title: tf(t, "mosqueMap", "خريطة") + " — " + tf(t, "availableLandsForMosque", "الأراضي"),
      description: tf(t, "serviceAvailableLandsDesc", ""),
      category: tf(t, "categoryElectronicServices", "خدمات"),
      keywords: ["خريطة", "أراضي", "map", "lands"],
    },
    {
      id: "page-login",
      path: "/benefaction/login",
      title: tf(t, "footerLogin", "تسجيل الدخول"),
      description: tf(t, "contactService", "خدمة تواصل"),
      category: tf(t, "categoryIndividualServices", "أفراد"),
      keywords: ["دخول", "login", "حساب", "account", "تسجيل"],
    },
    {
      id: "page-my-dashboard",
      path: "/my-dashboard",
      title: tf(t, "dashboard", "لوحة التحكم"),
      description: "",
      category: tf(t, "categoryIndividualServices", "أفراد"),
      keywords: ["لوحة", "dashboard", "حساب", "مؤشرات", "طلبات"],
    },
    {
      id: "page-my-requests",
      path: "/my-requests",
      title: "طلباتي",
      description: tf(t, "serviceContactDesc", ""),
      category: tf(t, "categoryIndividualServices", "أفراد"),
      keywords: ["طلبات", "requests", "متابعة", "طلب"],
    },
    {
      id: "page-my-profile",
      path: "/my-profile",
      title: tf(t, "siteSearchProfileTitle", "الملف التعريفي"),
      description: tf(t, "siteSearchProfileDesc", ""),
      category: tf(t, "categoryIndividualServices", "أفراد"),
      keywords: ["ملف", "profile", "بيانات", "حساب", "my-profile"],
    },
    {
      id: "page-donation-opportunities",
      path: "/mosque/donation-opportunities",
      title: tf(t, "serviceMosqueContribution", "فرص عقود الصيانة"),
      description: tf(t, "serviceMosqueContributionDesc", ""),
      category: tf(t, "categoryElectronicServices", "خدمات"),
      keywords: ["فرص", "صيانة", "تبرع", "opportunities"],
    },
  ];

  // Dedupe by path (service routes win first; skip duplicate paths from extra)
  const seen = new Set<string>();
  const merged: SiteSearchHit[] = [];
  for (const h of [...fromServices, ...extra]) {
    if (seen.has(h.path)) continue;
    seen.add(h.path);
    merged.push(h);
  }
  return merged;
}

/** Match query against searchable text; supports multi-word (all tokens must appear). */
export function hitMatchesQuery(hit: SiteSearchHit, rawQuery: string): boolean {
  const q = rawQuery.trim().toLowerCase();
  if (!q) return true;
  const blob = [hit.title, hit.description, hit.category, ...hit.keywords].join(" ").toLowerCase();
  if (blob.includes(q)) return true;
  const tokens = q.split(/\s+/).filter((w) => w.length > 0);
  if (tokens.length <= 1) return blob.includes(q);
  return tokens.every((tok) => blob.includes(tok));
}
